import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, FileSpreadsheet, FileText, ListOrdered, Users, X } from "lucide-react";
import RelayPdfExport from "./RelayPdfExport";
import MatchProgram from "./MatchProgram";
import "./MatchDownloads.css";

// This catalog is deliberately public-only. Private report definitions live in the director workspace.
export function publicMatchDocuments(family, canExportResults, canExportStandings = true) {
  return [
    ...(canExportResults && canExportStandings ? [{ id: "standings", title: "Standings", detail: family === "bullseye" ? "PDF · Simple score listing" : "Print · Match standings", icon: ListOrdered }] : []),
    ...(family === "bullseye" && canExportResults ? [
      { id: "bulletin", title: "Results bulletin", detail: "PDF · Published results and award groupings", icon: FileText },
      { id: "scores", title: "Scores spreadsheet", detail: "CSV · All competitors and score sections", icon: FileSpreadsheet },
    ] : []),
    ...(family === "bullseye" ? [{ id: "relays", title: "Relay assignments", detail: "PDF · All relays and firing points", icon: Users }] : []),
    { id: "program", title: "Match program", detail: "PDF · Published by the match director", icon: FileText },
  ];
}

export default function PublicMatchDownloads({ match, family, canExportResults = false, standings, bulletin, scores }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dialog = useRef(null);
  const trigger = useRef(null);
  const titleId = useId();
  const requested = new URLSearchParams(location.search).get("download");
  const open = Boolean(requested);
  const docs = publicMatchDocuments(family, canExportResults, family === "bullseye" || Boolean(standings));
  const selected = docs.find(doc => doc.id === requested);
  const path = `/matches/${family}/${encodeURIComponent(match.matchId)}`;
  const localContent = { standings, bulletin, scores,
    relays: <RelayPdfExport match={match} />,
    program: <MatchProgram matchId={match.matchId} family={family} showEmpty />,
  };
  function select(id) {
    if (["standings", "bulletin", "scores"].includes(id) && !localContent[id]) {
      const target = ["bulletin", "scores"].includes(id) ? `${path}/results` : path;
      if (target !== location.pathname) {
        navigate(`${target}?download=${id}`, { state: location.state });
        return;
      }
    }
    const params = new URLSearchParams(location.search);
    if (id) params.set("download", id); else params.delete("download");
    navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash }, { replace: true, state: location.state });
  }
  useEffect(() => {
    if (!open) return undefined;
    const node = dialog.current;
    const previousFocus = document.activeElement;
    node.showModal();
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      node.close();
      document.body.style.overflow = oldOverflow;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [open]);
  const destination = selected && `${["bulletin", "scores"].includes(selected.id) ? `${path}/results` : path}?download=${selected.id}`;
  return <>
    <button ref={trigger} type="button" className="match-download-trigger" aria-haspopup="dialog" onClick={() => select("all")}>
      <Download size={17} aria-hidden="true" />Downloads
    </button>
    {open && createPortal(<dialog ref={dialog} className="match-download-dialog" aria-labelledby={titleId}
      onCancel={event => { event.preventDefault(); select(null); }} onClick={event => { if (event.target === event.currentTarget) select(null); }}>
      <div className="match-download-surface">
        <header className="match-download-heading">
          <div><span>Match downloads</span><h2 id={titleId}>{selected?.title || "Downloads"}</h2></div>
          <button type="button" className="match-download-icon" aria-label="Close downloads" onClick={() => select(null)}><X size={21} /></button>
        </header>
        <p className="match-download-name">{match.matchName || match.name || match.title}</p>
        {selected ? <>
          <button type="button" className="match-download-back" onClick={() => select("all")}><ArrowLeft size={15} />All downloads</button>
          <div className="match-download-options" key={`${match.matchId}:${selected.id}`}>
            {localContent[selected.id] || <Link className="match-download-continue" to={destination} state={location.state}>Choose {selected.title.toLowerCase()} options<ArrowRight size={17} /></Link>}
          </div>
        </> : requested === "all" ? <div className="match-download-list">{docs.map(({ id, title, detail, icon: Icon }) =>
          <button key={id} type="button" className="match-download-row" onClick={() => select(id)}>
            <Icon size={22} aria-hidden="true" /><span><strong>{title}</strong><small>{detail}</small></span><ArrowRight size={17} aria-hidden="true" />
          </button>)}</div> : <div className="match-download-unavailable"><p>This document is not available for this match.</p><button type="button" onClick={() => select("all")}>View available downloads</button></div>}
        <footer className="match-download-footer">Public match documents</footer>
      </div>
    </dialog>, document.body)}
  </>;
}
